/**
 * FEA-029: Terminal Chat Panel
 *
 * Embedded xterm.js terminal that connects to the backend PTY service.
 * Features:
 * - Dockable/draggable window (default: bottom-right, 50% height)
 * - Settings panel (theme, font, cursor, behavior)
 * - Context selection mode (click UI elements to paste context)
 * - Persistent session across minimize/reopen
 */

import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { logger } from '@/utils/logger';
import { useLocation } from 'react-router-dom';
import { Terminal } from '@xterm/xterm';
import { FitAddon } from '@xterm/addon-fit';
import { WebLinksAddon } from '@xterm/addon-web-links';
import '@xterm/xterm/css/xterm.css';
import './TerminalChatPanel.css';
import { useTerminalSettings } from '../../hooks/useTerminalSettings';
import { useTerminology } from '@/hooks/useTerminology';
import { getThemeByName, TERMINAL_THEMES, FONT_FAMILIES } from '../../utils/terminalThemes';

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';
type PanelState = 'docked' | 'floating' | 'minimized';

export function TerminalChatPanel() {
  const { settings, updateSetting, updateSettings } = useTerminalSettings();
  const location = useLocation();
  const { t } = useTerminology();

  // Map routes to friendly page names for context
  const PAGE_NAMES = useMemo<Record<string, string>>(
    () => ({
      // sysview-wave2 — '/' and '/components' redirect to the Interlock shell now
      // (this panel doesn't mount there); entries kept only for the redirect flash.
      '/': 'Dashboard',
      '/components': 'Components',
      '/failure-modes': t.failureModes,
      '/causes': 'Causes',
      '/effects': 'Effects',
      '/mitigations': t.mitigations,
      '/requirements': t.requirements,
      '/cause-effect': 'Cause & Effect Analysis',
      '/block-diagram': 'Block Diagram',
      '/system-states': 'System States',
      '/rpn-history': `${t.rpn} History`,
      '/reports': 'Reports',
    }),
    [t]
  );

  const [isOpen, setIsOpen] = useState(false); // Default minimized
  const [panelState, setPanelState] = useState<PanelState>('docked');
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [showSettings, setShowSettings] = useState(false);
  const [contextMode, setContextMode] = useState(false);

  // Floating position
  const [floatingPos, setFloatingPos] = useState({
    x: settings.floatingX,
    y: settings.floatingY,
    width: settings.floatingWidth,
    height: settings.floatingHeight,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [resizeEdge, setResizeEdge] = useState<string | null>(null);

  const terminalRef = useRef<HTMLDivElement>(null);
  const terminalInstance = useRef<Terminal | null>(null);
  const fitAddon = useRef<FitAddon | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  // sysview-wave2 lint fix — `NodeJS.Timeout` is a no-undef in this browser-env
  // eslint config; `ReturnType<typeof setTimeout>` is the house recipe (same as
  // ExpandableFunctionTile / ExpandableFMTile / AddRuleWizard).
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef<string>(location.pathname);

  // Track if we're in the process of connecting to prevent duplicates
  const connectingRef = useRef(false);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Prevent duplicate connections - check both OPEN and CONNECTING states
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      connectingRef.current
    ) {
      return;
    }

    connectingRef.current = true;
    setStatus('connecting');

    // Build WebSocket URL - use proxy path on tunnel, direct connection otherwise
    const isTunnel = window.location.hostname === 'fmea-studio.com';
    const wsUrl = isTunnel
      ? `wss://${window.location.hostname}/ws-terminal`
      : `ws://${window.location.hostname}:8003`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      connectingRef.current = false;
      setStatus('connected');

      if (terminalInstance.current) {
        ws.send(
          JSON.stringify({
            type: 'resize',
            cols: terminalInstance.current.cols,
            rows: terminalInstance.current.rows,
          })
        );
      }
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        if (msg.type === 'output' && terminalInstance.current) {
          terminalInstance.current.write(msg.data);
        } else if (msg.type === 'exit') {
          terminalInstance.current?.write('\r\n[Session ended]\r\n');
          setStatus('disconnected');
        }
        // Ignore other message types silently
      } catch (e) {
        // Only write raw data if it's actually plain text output (not JSON parse error)
        // This prevents accidental double-writes
        logger.warn('Failed to parse message', e, { module: 'TerminalChatPanel' });
      }
    };

    ws.onclose = () => {
      connectingRef.current = false;
      setStatus('disconnected');
      wsRef.current = null;

      if (isOpen && panelState !== 'minimized') {
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 2000);
      }
    };

    ws.onerror = (error) => {
      logger.error('WebSocket error', error, { module: 'TerminalChatPanel' });
      connectingRef.current = false;
      setStatus('error');
    };
  }, [isOpen, panelState]);

  // Track if terminal event handlers are set up (to prevent duplicates)
  const handlersSetupRef = useRef(false);

  // Initialize terminal - runs once when panel opens
  // Note: settings is NOT a dependency - settings changes are handled by a separate effect
  useEffect(() => {
    if (!isOpen || !terminalRef.current) return;

    if (!terminalInstance.current) {
      // BUG FIX: xterm's open() triggers RenderService which needs container dimensions
      // Delay initialization until container has layout to avoid "Cannot read properties of undefined (reading 'dimensions')"
      const container = terminalRef.current;

      const initializeTerminal = () => {
        if (!container || terminalInstance.current) return;

        const term = new Terminal({
          theme: getThemeByName(settings.theme),
          fontFamily:
            FONT_FAMILIES.find((f) => f.name === settings.fontFamily)?.value || settings.fontFamily,
          fontSize: settings.fontSize,
          lineHeight: settings.lineHeight,
          cursorBlink: settings.cursorBlink,
          cursorStyle: settings.cursorStyle,
          scrollback: settings.scrollback,
          convertEol: true,
          // Prevent potential echo issues
          windowsMode: false,
          screenReaderMode: false,
        });

        const fit = new FitAddon();
        fitAddon.current = fit;
        term.loadAddon(fit);

        const webLinks = new WebLinksAddon();
        term.loadAddon(webLinks);

        term.open(container);
        fit.fit();

        terminalInstance.current = term;

        // Set up event handlers only once
        if (!handlersSetupRef.current) {
          handlersSetupRef.current = true;

          term.onData((data) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'input', data }));
            }
          });

          term.onResize(({ cols, rows }) => {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
            }
          });
        }

        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          connect();
        }
      };

      // Check if container already has dimensions
      if (container.offsetWidth > 0 && container.offsetHeight > 0) {
        initializeTerminal();
      } else {
        // Wait for container to have dimensions using ResizeObserver
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
              resizeObserver.disconnect();
              initializeTerminal();
              break;
            }
          }
        });
        resizeObserver.observe(container);

        // Cleanup if effect reruns before observer triggers
        return () => resizeObserver.disconnect();
      }

      return; // Skip the rest - handled inside initializeTerminal
    }

    // Terminal already exists - handle re-attachment or connection
    if (terminalRef.current && !terminalRef.current.hasChildNodes()) {
      // Terminal exists but needs to be re-attached to DOM (after minimize/restore)
      fitAddon.current?.fit();
    }

    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      connect();
    }

    const fitTimeout = setTimeout(() => {
      fitAddon.current?.fit();
      terminalInstance.current?.focus();
    }, 100);

    return () => {
      // Clear all timeouts
      clearTimeout(fitTimeout);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      // Close WebSocket connection on unmount
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      // Dispose terminal instance to free memory
      if (terminalInstance.current) {
        terminalInstance.current.dispose();
        terminalInstance.current = null;
      }
    };
    // NOTE: deps intentionally omit some referenced values (legacy behavior).
    // This used to carry an eslint-disable for react-hooks/exhaustive-deps, but
    // that plugin isn't configured in this repo's eslint config, so the disable
    // comment itself was a "definition not found" lint ERROR (sysview-wave2 fix).
  }, [isOpen, panelState, connect]);

  // Update terminal settings when they change
  useEffect(() => {
    if (terminalInstance.current) {
      terminalInstance.current.options.theme = getThemeByName(settings.theme);
      terminalInstance.current.options.fontSize = settings.fontSize;
      terminalInstance.current.options.fontFamily =
        FONT_FAMILIES.find((f) => f.name === settings.fontFamily)?.value || settings.fontFamily;
      terminalInstance.current.options.lineHeight = settings.lineHeight;
      terminalInstance.current.options.cursorBlink = settings.cursorBlink;
      terminalInstance.current.options.cursorStyle = settings.cursorStyle;
      fitAddon.current?.fit();
    }
  }, [
    settings.theme,
    settings.fontSize,
    settings.fontFamily,
    settings.lineHeight,
    settings.cursorBlink,
    settings.cursorStyle,
  ]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && panelState !== 'minimized' && fitAddon.current) {
        fitAddon.current.fit();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, panelState]);

  // Navigation context - send message when user navigates to different pages
  useEffect(() => {
    // Skip if feature is disabled or websocket not connected
    if (!settings.navigationContext || wsRef.current?.readyState !== WebSocket.OPEN) {
      prevPathRef.current = location.pathname;
      return;
    }

    // Skip initial mount (only track navigation changes)
    if (prevPathRef.current === location.pathname) {
      return;
    }

    // Get friendly page name
    const pageName = PAGE_NAMES[location.pathname] || location.pathname;

    // Send navigation context message and then auto-submit
    // Claude Code's multi-line input requires Enter on empty line to submit
    // We send text first, then Enter keypresses separately with delays
    const text = `[Navigation: User is now viewing "${pageName}" page]`;
    const ws = wsRef.current;

    // Send the text
    ws.send(JSON.stringify({ type: 'input', data: text }));

    // After small delay, send Enter (end the line)
    setTimeout(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input', data: '\r' }));
      }
    }, 50);

    // After another delay, send Enter again (submit on empty line)
    setTimeout(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'input', data: '\r' }));
      }
    }, 100);

    // Update previous path
    prevPathRef.current = location.pathname;
  }, [location.pathname, settings.navigationContext]);

  // Drag handling
  const handleDragStart = useCallback(
    (e: React.MouseEvent) => {
      if (panelState === 'docked') {
        // Undock and start dragging
        const rect = panelRef.current?.getBoundingClientRect();
        if (rect) {
          const newX = e.clientX - rect.width / 2;
          const newY = e.clientY - 20;
          setFloatingPos({
            x: newX,
            y: newY,
            width: rect.width,
            height: rect.height,
          });
          // Use the NEW position for drag offset when undocking
          setDragOffset({ x: e.clientX - newX, y: e.clientY - newY });
        }
        setPanelState('floating');
        setIsDragging(true);
      } else {
        // Already floating - use current position
        setIsDragging(true);
        setDragOffset({ x: e.clientX - floatingPos.x, y: e.clientY - floatingPos.y });
      }
    },
    [panelState, floatingPos]
  );

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Check if near dock zone (bottom-right corner)
      const nearDock = e.clientX > window.innerWidth - 100 && e.clientY > window.innerHeight - 100;

      setFloatingPos((prev) => ({ ...prev, x: newX, y: newY }));

      // Visual feedback for dock zone could be added here
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsDragging(false);

      // Auto-dock if dropped near bottom-right corner
      if (e.clientX > window.innerWidth - 100 && e.clientY > window.innerHeight - 100) {
        setPanelState('docked');
      } else if (settings.rememberPosition) {
        updateSettings({
          floatingX: floatingPos.x,
          floatingY: floatingPos.y,
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset, floatingPos, settings.rememberPosition, updateSettings]);

  // Resize handling
  const handleResizeStart = useCallback((e: React.MouseEvent, edge: string) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeEdge(edge);
  }, []);

  useEffect(() => {
    if (!isResizing || !resizeEdge) return;

    const handleMouseMove = (e: MouseEvent) => {
      setFloatingPos((prev) => {
        let newPos = { ...prev };

        if (resizeEdge.includes('e')) {
          newPos.width = Math.max(400, e.clientX - prev.x);
        }
        if (resizeEdge.includes('w')) {
          const newWidth = Math.max(400, prev.x + prev.width - e.clientX);
          newPos.x = prev.x + prev.width - newWidth;
          newPos.width = newWidth;
        }
        if (resizeEdge.includes('s')) {
          newPos.height = Math.max(300, e.clientY - prev.y);
        }
        if (resizeEdge.includes('n')) {
          const newHeight = Math.max(300, prev.y + prev.height - e.clientY);
          newPos.y = prev.y + prev.height - newHeight;
          newPos.height = newHeight;
        }

        return newPos;
      });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeEdge(null);

      if (settings.rememberPosition) {
        updateSettings({
          floatingWidth: floatingPos.width,
          floatingHeight: floatingPos.height,
        });
      }

      fitAddon.current?.fit();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, resizeEdge, floatingPos, settings.rememberPosition, updateSettings]);

  // Context mode - add listener for element clicks
  useEffect(() => {
    if (!contextMode) return;

    const handleContextClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Skip clicks on the terminal panel itself
      if (panelRef.current?.contains(target)) {
        return;
      }

      // First try explicit data-context attribute
      const contextEl = target.closest('[data-context]');
      let contextData: string | null = null;

      if (contextEl) {
        contextData = contextEl.getAttribute('data-context');
      } else {
        // Fallback: extract meaningful text from clicked element or parents
        // Look for common patterns: table cells, labels, headings, buttons, spans
        const meaningfulElement = target.closest(
          'td, th, label, h1, h2, h3, h4, h5, h6, button, [role="button"], .ag-cell, .node-label, span, p, div'
        );

        if (meaningfulElement) {
          // Get direct text content, not children's text
          const text = getDirectTextContent(meaningfulElement as HTMLElement);
          if (text && text.length > 0 && text.length < 500) {
            contextData = text;
          }
        }
      }

      if (contextData && wsRef.current?.readyState === WebSocket.OPEN) {
        e.preventDefault();
        e.stopPropagation();

        // Prefix with context indicator so the AI understands this is UI context
        const prefixed = `[Context: "${contextData}"]`;
        const formatted = settings.contextAutoNewline ? prefixed + '\n' : prefixed;
        wsRef.current.send(JSON.stringify({ type: 'input', data: formatted }));

        if (settings.contextAutoExit) {
          setContextMode(false);
        }

        // Focus the terminal so user can immediately start typing
        setTimeout(() => {
          terminalInstance.current?.focus();
        }, 50);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setContextMode(false);
      }
    };

    document.addEventListener('click', handleContextClick, true);
    document.addEventListener('keydown', handleKeyDown);
    document.body.classList.add('terminal-context-mode');

    return () => {
      document.removeEventListener('click', handleContextClick, true);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.classList.remove('terminal-context-mode');
    };
  }, [contextMode, settings.contextAutoExit, settings.contextAutoNewline]);

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleMinimize = () => setPanelState('minimized');
  const handleRestore = () => setPanelState('docked');
  const handleToggleDock = () => {
    if (panelState === 'docked') {
      setPanelState('floating');
    } else {
      setPanelState('docked');
    }
  };

  // Panel styles based on state
  const panelStyle: React.CSSProperties =
    panelState === 'floating'
      ? {
          position: 'fixed',
          left: floatingPos.x,
          top: floatingPos.y,
          width: floatingPos.width,
          height: floatingPos.height,
          opacity: settings.floatingOpacity / 100,
        }
      : {};

  return (
    <>
      {/* FAB Button (hidden when open) - Compact robot emoji with status LED */}
      <button
        className={`terminal-chat-fab ${isOpen ? 'hidden' : ''}`}
        onClick={handleToggle}
        aria-label="Open FMEA Assistant"
        title="Open FMEA Assistant"
      >
        🤖
        <span
          className="terminal-chat-fab-badge"
          style={{
            background:
              status === 'connected' ? '#22c55e' : status === 'connecting' ? '#f59e0b' : '#ef4444',
          }}
        />
      </button>

      {/* Minimized Pill - Compact with robot emoji */}
      {panelState === 'minimized' && isOpen && (
        <button className="terminal-minimized-pill" onClick={handleRestore}>
          🤖
          <span
            className="terminal-pill-badge"
            style={{
              background:
                status === 'connected'
                  ? '#22c55e'
                  : status === 'connecting'
                    ? '#f59e0b'
                    : '#ef4444',
            }}
          />
        </button>
      )}

      {/* Terminal Panel */}
      <div
        ref={panelRef}
        className={`terminal-chat-panel ${isOpen ? 'open' : ''} ${panelState}`}
        style={panelStyle}
      >
        {/* Resize handles for floating mode */}
        {panelState === 'floating' && (
          <>
            <div
              className="resize-handle resize-n"
              onMouseDown={(e) => handleResizeStart(e, 'n')}
            />
            <div
              className="resize-handle resize-e"
              onMouseDown={(e) => handleResizeStart(e, 'e')}
            />
            <div
              className="resize-handle resize-s"
              onMouseDown={(e) => handleResizeStart(e, 's')}
            />
            <div
              className="resize-handle resize-w"
              onMouseDown={(e) => handleResizeStart(e, 'w')}
            />
            <div
              className="resize-handle resize-ne"
              onMouseDown={(e) => handleResizeStart(e, 'ne')}
            />
            <div
              className="resize-handle resize-se"
              onMouseDown={(e) => handleResizeStart(e, 'se')}
            />
            <div
              className="resize-handle resize-sw"
              onMouseDown={(e) => handleResizeStart(e, 'sw')}
            />
            <div
              className="resize-handle resize-nw"
              onMouseDown={(e) => handleResizeStart(e, 'nw')}
            />
          </>
        )}

        {/* Header */}
        <div
          className="terminal-chat-header"
          onMouseDown={handleDragStart}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        >
          <div className="terminal-chat-title">
            <span className="drag-handle">≡</span>
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="4,17 10,11 4,5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            <span>FMEA Assistant</span>
          </div>
          <div className="terminal-chat-header-actions" onMouseDown={(e) => e.stopPropagation()}>
            <button
              className={`terminal-header-btn ${contextMode ? 'active' : ''}`}
              onClick={() => setContextMode(!contextMode)}
              title="Context selection mode"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
            </button>
            <button
              className="terminal-header-btn"
              onClick={() => {
                if (wsRef.current?.readyState === WebSocket.OPEN) {
                  const message =
                    'Read docs/CHATBOT_PERSONA.md and adopt that persona for this conversation.';
                  wsRef.current.send(JSON.stringify({ type: 'input', data: message }));
                  // Send Enter to end line, then Enter again to submit
                  setTimeout(() => {
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ type: 'input', data: '\r' }));
                    }
                  }, 50);
                  setTimeout(() => {
                    if (wsRef.current?.readyState === WebSocket.OPEN) {
                      wsRef.current.send(JSON.stringify({ type: 'input', data: '\r' }));
                    }
                  }, 100);
                }
              }}
              title="Load FMEA Assistant persona"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </button>
            <button
              className={`terminal-header-btn ${showSettings ? 'active' : ''}`}
              onClick={() => setShowSettings(!showSettings)}
              title="Settings"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </button>
            <button
              className="terminal-header-btn"
              onClick={handleToggleDock}
              title={panelState === 'docked' ? 'Float' : 'Dock'}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                {panelState === 'docked' ? (
                  <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
                ) : (
                  <path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l3-3-3-3M19 9l-3-3-3 3" />
                )}
              </svg>
            </button>
            <ConnectionStatusIndicator status={status} />
            <button className="terminal-header-btn" onClick={handleMinimize} title="Minimize">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M5 12h14" />
              </svg>
            </button>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && (
          <div className="terminal-settings">
            <div className="terminal-settings-section">
              <h4>Appearance</h4>
              <div className="terminal-settings-row">
                <label>Theme</label>
                <select
                  value={settings.theme}
                  onChange={(e) => updateSetting('theme', e.target.value)}
                >
                  {TERMINAL_THEMES.map((t) => (
                    <option key={t.name} value={t.name}>
                      {t.displayName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="terminal-settings-row">
                <label>Font Size</label>
                <input
                  type="range"
                  min="10"
                  max="24"
                  value={settings.fontSize}
                  onChange={(e) => updateSetting('fontSize', parseInt(e.target.value))}
                />
                <span>{settings.fontSize}px</span>
              </div>
              <div className="terminal-settings-row">
                <label>Font</label>
                <select
                  value={settings.fontFamily}
                  onChange={(e) => updateSetting('fontFamily', e.target.value)}
                >
                  {FONT_FAMILIES.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="terminal-settings-row">
                <label>Line Height</label>
                <input
                  type="range"
                  min="1"
                  max="2"
                  step="0.1"
                  value={settings.lineHeight}
                  onChange={(e) => updateSetting('lineHeight', parseFloat(e.target.value))}
                />
                <span>{settings.lineHeight.toFixed(1)}</span>
              </div>
            </div>

            <div className="terminal-settings-section">
              <h4>Cursor</h4>
              <div className="terminal-settings-row">
                <label>Style</label>
                <select
                  value={settings.cursorStyle}
                  onChange={(e) =>
                    updateSetting('cursorStyle', e.target.value as 'bar' | 'block' | 'underline')
                  }
                >
                  <option value="bar">Bar</option>
                  <option value="block">Block</option>
                  <option value="underline">Underline</option>
                </select>
              </div>
              <div className="terminal-settings-row">
                <label>Blink</label>
                <input
                  type="checkbox"
                  checked={settings.cursorBlink}
                  onChange={(e) => updateSetting('cursorBlink', e.target.checked)}
                />
              </div>
            </div>

            <div className="terminal-settings-section">
              <h4>Behavior</h4>
              <div className="terminal-settings-row">
                <label>Scrollback</label>
                <input
                  type="number"
                  min="1000"
                  max="100000"
                  step="1000"
                  value={settings.scrollback}
                  onChange={(e) => updateSetting('scrollback', parseInt(e.target.value))}
                />
              </div>
              <div className="terminal-settings-row">
                <label>Copy on Select</label>
                <input
                  type="checkbox"
                  checked={settings.copyOnSelect}
                  onChange={(e) => updateSetting('copyOnSelect', e.target.checked)}
                />
              </div>
              <div className="terminal-settings-row">
                <label>Bell Sound</label>
                <input
                  type="checkbox"
                  checked={settings.bellSound}
                  onChange={(e) => updateSetting('bellSound', e.target.checked)}
                />
              </div>
            </div>

            <div className="terminal-settings-section">
              <h4>Context Selection</h4>
              <div className="terminal-settings-row">
                <label>Auto-exit after select</label>
                <input
                  type="checkbox"
                  checked={settings.contextAutoExit}
                  onChange={(e) => updateSetting('contextAutoExit', e.target.checked)}
                />
              </div>
              <div className="terminal-settings-row">
                <label>Add newline after paste</label>
                <input
                  type="checkbox"
                  checked={settings.contextAutoNewline}
                  onChange={(e) => updateSetting('contextAutoNewline', e.target.checked)}
                />
              </div>
              <div className="terminal-settings-row">
                <label>Send navigation context</label>
                <input
                  type="checkbox"
                  checked={settings.navigationContext}
                  onChange={(e) => updateSetting('navigationContext', e.target.checked)}
                />
              </div>
            </div>
          </div>
        )}

        {/* Context Mode Toast */}
        {contextMode && (
          <div className="terminal-context-toast">
            Context mode active - click any element (Esc to cancel)
          </div>
        )}

        {/* Terminal Container */}
        <div className="terminal-chat-body">
          <div ref={terminalRef} className="terminal-chat-terminal" />
        </div>
      </div>
    </>
  );
}

function ConnectionStatusIndicator({ status }: { status: ConnectionStatus }) {
  const statusColors: Record<ConnectionStatus, string> = {
    connected: '#22c55e',
    connecting: '#f59e0b',
    disconnected: '#ef4444',
    error: '#ef4444',
  };

  return (
    <div
      className="terminal-chat-status-dot"
      style={{ backgroundColor: statusColors[status] }}
      title={status}
    />
  );
}

/**
 * Extract direct text content from an element, excluding deeply nested text.
 * This prevents grabbing entire page content when clicking on a container.
 */
function getDirectTextContent(element: HTMLElement): string {
  // For simple elements like buttons, headings, etc., just use textContent
  const simpleElements = ['BUTTON', 'TH', 'LABEL', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'SPAN', 'A'];
  if (simpleElements.includes(element.tagName)) {
    const text = element.textContent?.trim() || '';
    // Don't return if it's too long (probably a container)
    if (text.length < 200) {
      return text;
    }
  }

  // For table cells, get the cell's text content
  if (element.tagName === 'TD' || element.classList.contains('ag-cell')) {
    const text = element.textContent?.trim() || '';
    if (text.length < 200) {
      return text;
    }
  }

  // For other elements, try to get only direct text nodes (not nested element text)
  let directText = '';
  for (const node of element.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      directText += node.textContent;
    }
  }
  directText = directText.trim();

  // If we found direct text, use it
  if (directText.length > 0 && directText.length < 200) {
    return directText;
  }

  // Fallback: if element has only one child that's an element, recurse
  if (element.children.length === 1 && element.childNodes.length === 1) {
    return getDirectTextContent(element.children[0] as HTMLElement);
  }

  // Last resort: get textContent if it's short enough
  const fullText = element.textContent?.trim() || '';
  if (fullText.length < 100) {
    return fullText;
  }

  return '';
}
